import { useCallback, useState } from "react";

interface UseFunnelOptions<Steps extends readonly string[]> {
  initialStep?: Steps[number];
}

interface UseFunnelReturn<Steps extends readonly string[]> {
  currentStep: Steps[number];
  currentIndex: number;
  totalSteps: number;
  progress: number;
  setStep: (step: Steps[number]) => void;
  goNext: () => void;
  goBack: () => void;
  hasNext: boolean;
  hasPrev: boolean;
}

export function useFunnel<const Steps extends readonly string[]>(
  steps: Steps,
  options?: UseFunnelOptions<Steps>,
): UseFunnelReturn<Steps> {
  const firstStep = steps[0];
  if (!firstStep) {
    throw new Error("useFunnel: steps array must contain at least one step");
  }
  const [currentStep, setCurrentStep] = useState<Steps[number]>(options?.initialStep ?? firstStep);

  const currentIndex = steps.indexOf(currentStep);
  const totalSteps = steps.length;
  const progress = totalSteps > 0 ? ((currentIndex + 1) / totalSteps) * 100 : 0;
  const hasNext = currentIndex < totalSteps - 1;
  const hasPrev = currentIndex > 0;

  const setStep = useCallback(
    (step: Steps[number]) => {
      if (steps.includes(step)) {
        setCurrentStep(step);
      }
    },
    [steps],
  );

  const goNext = useCallback(() => {
    setCurrentStep((prev) => {
      const idx = steps.indexOf(prev);
      return steps[idx + 1] ?? prev;
    });
  }, [steps]);

  const goBack = useCallback(() => {
    setCurrentStep((prev) => {
      const idx = steps.indexOf(prev);
      return steps[idx - 1] ?? prev;
    });
  }, [steps]);

  return {
    currentStep,
    currentIndex,
    totalSteps,
    progress,
    setStep,
    goNext,
    goBack,
    hasNext,
    hasPrev,
  };
}
